import { useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from "react";
import { message } from "antd";
import { AppContext } from "../../../context/AppContext";
import useHttp from "../../../hooks/useHttp";
import { successNotification } from "../../utils/CommonNotifications";
import type { ServerWorkflow } from "../parser";
import {
    extractWorkflowPayload,
    getWorkflowApiErrorMessage,
    isWorkflowApiLogicalFailure,
    parseFetchDataEnvelope,
} from "../utils/workflowApiResponse";

export type WorkflowTriggerType = "Trigger.Message.Inbound" | "Trigger.Message.Outbound" | "INBOUND" | "OUTBOUND";

export interface WorkflowApiRecord {
    osyncId?: string;
    workflowId: string;
    name: string;
    status: string;
    triggerType: WorkflowTriggerType;
    revision?: number;
    entryNodeId?: string;
    applyOncePerContact?: boolean;
    defaultFlow?: boolean;
    createdAt?: string;
    updatedAt?: string;
    totalRunsCount?: number | null;
    count?: number | null;
}

export interface WorkflowRecord {
    workflowId: string;
    workflowName: string;
    status: string;
    triggerType: string;
    direction: "Incoming" | "Outgoing";
    runs: number;
    defaultFlow?: boolean;
    createdTime?: string;
    updatedTime?: string;
}

export type FlowTabKey = "custom" | "predefined";

export type TriggerTypeFilterValue = "Trigger.Message.Inbound" | "Trigger.Message.Outbound" | null;

const PAGE_SIZE = 10;

function isRequestCanceledOrAborted(error: unknown, signal?: AbortSignal): boolean {
    if (signal?.aborted) return true;
    if (typeof error !== "object" || error === null) return false;
    const candidate = error as { name?: string; code?: string; message?: string };
    return (
        candidate.name === "AbortError" ||
        candidate.code === "ERR_CANCELED" ||
        candidate.message === "canceled"
    );
}

function mapApiToWorkflowRecord(api: WorkflowApiRecord): WorkflowRecord {
    const isInbound = api.triggerType?.toUpperCase().includes("INBOUND") ?? false;
    const direction: WorkflowRecord["direction"] = isInbound ? "Incoming" : "Outgoing";
    const runs =
        typeof api.totalRunsCount === "number" ? api.totalRunsCount : typeof api.count === "number" ? api.count : 0;
    return {
        workflowId: api.workflowId,
        workflowName: api.name,
        status: api.status,
        triggerType: api.triggerType,
        direction,
        runs,
        defaultFlow: api.defaultFlow ?? false,
        createdTime: api.createdAt,
        updatedTime: api.updatedAt,
    };
}

export const TRIGGER_FILTER_OPTIONS = [
    { label: "All", value: "" },
    { label: "Incoming", value: "Trigger.Message.Inbound" },
    { label: "Outgoing", value: "Trigger.Message.Outbound" },
];

export const STATUS_FILTER_OPTIONS = [
    { label: "All", value: "" },
    { label: "Active", value: "ACTIVE" },
    { label: "Disabled", value: "DISABLED" },
    { label: "Draft", value: "DRAFT" },
];

export interface UseWorkflowsResult {
    requestWorkflows: (params: {
        cursor?: { cursor?: string; lastKey?: string; lastRange?: string };
        limit?: number;
        signal?: AbortSignal;
        searchKeyword?: string;
        flowType?: FlowTabKey;
        triggerType?: TriggerTypeFilterValue;
        status?: string | null;
    }) => Promise<{ data: WorkflowRecord[]; success: boolean; nextCursor: unknown }>;
    activeTab: FlowTabKey;
    setActiveTab: (key: FlowTabKey) => void;
    searchInputValue: string;
    setSearchInputValue: (v: string) => void;
    appliedSearchKeyword: string;
    triggerTypeFilter: TriggerTypeFilterValue;
    statusFilter: string | null;
    handleTriggerTypeFilterChange: (value: string) => void;
    handleStatusFilterChange: (value: string) => void;
    handleSearchApply: () => void;
    handleSearchClear: () => void;
    handleTabChange: (key: string) => void;
    enableWorkflow: (record: WorkflowRecord) => Promise<void>;
    disableWorkflow: (record: WorkflowRecord) => Promise<void>;
    deleteWorkflow: (record: WorkflowRecord) => Promise<void>;
    reload: () => void;
    tableRef: React.RefObject<{ reload?: () => void } | null>;
    currentPageData: WorkflowRecord[];
}

export function useWorkflows(): UseWorkflowsResult {
    const [appProperties] = useContext(AppContext);
    const { fetchDataByPagination, fetchData } = useHttp();
    const tableRef = useRef<{ reload?: () => void } | null>(null);

    const [activeTab, setActiveTab] = useState<FlowTabKey>("custom");
    const [searchInputValue, setSearchInputValue] = useState("");
    const [appliedSearchKeyword, setAppliedSearchKeyword] = useState("");
    const [triggerTypeFilter, setTriggerTypeFilter] = useState<TriggerTypeFilterValue>(null);
    const [statusFilter, setStatusFilter] = useState<string | null>(null);
    const [currentPageData, setCurrentPageData] = useState<WorkflowRecord[]>([]);

    const reload = useCallback(() => {
        tableRef.current?.reload?.();
    }, []);

    const requestWorkflows = useCallback(
        async (params: {
            cursor?: { cursor?: string; lastKey?: string; lastRange?: string };
            limit?: number;
            signal?: AbortSignal;
            searchKeyword?: string;
            flowType?: FlowTabKey;
            triggerType?: TriggerTypeFilterValue;
            status?: string | null;
        }) => {
            const { limit, cursor, signal, searchKeyword, flowType, triggerType, status } = params;
            try {
                const searchParams = new URLSearchParams();
                if (searchKeyword?.trim()) {
                    searchParams.set("searchText", searchKeyword.trim());
                }
                if (triggerType) {
                    searchParams.set("triggerType", triggerType);
                }
                if (status?.trim()) {
                    searchParams.set("status", status === "DISABLED" ? "PAUSED" : status.trim()); // server uses PAUSED for disabled
                }
                if (flowType === "predefined") {
                    searchParams.set("defaultFlow", "true");
                }
                const query = searchParams.toString();
                const url = query ? `workflows?${query}` : "workflows";

                const res = await fetchDataByPagination(
                    url,
                    "GET",
                    null,
                    appProperties,
                    null,
                    limit ?? PAGE_SIZE,
                    cursor ?? null,
                    null,
                    signal
                );
                if (signal?.aborted) return { data: [], success: false, nextCursor: null };

                if (res == null) {
                    setCurrentPageData([]);
                    message.error("Failed to load rules. Please try again.");
                    return { data: [], success: false, nextCursor: null };
                }

                const rawItems = Array.isArray(res) ? res : (res?.data ?? res?.workflows ?? res?.items ?? []);
                const pagination = res?.paginationModel ?? res?.pagination;
                const nextCursor = pagination?.cursor
                    ? { cursor: pagination.cursor }
                    : pagination?.lastKey
                      ? { lastKey: pagination.lastKey, lastRange: pagination.lastRange ?? null }
                      : null;

                const mapped = rawItems.map((item: WorkflowApiRecord) => mapApiToWorkflowRecord(item));
                setCurrentPageData(mapped);

                return { data: mapped, success: true, nextCursor };
            } catch (error) {
                setCurrentPageData([]);
                if (!isRequestCanceledOrAborted(error, signal)) {
                    message.error("Failed to load rules. Please try again.");
                }
                return { data: [], success: false, nextCursor: null };
            }
        },
        [appProperties, fetchDataByPagination]
    );

    const handleTriggerTypeFilterChange = useCallback((value: string) => {
        const v = value?.trim() ?? "";
        setTriggerTypeFilter(v ? (v as TriggerTypeFilterValue) : null);
        requestAnimationFrame(() => tableRef.current?.reload?.());
    }, []);

    const handleStatusFilterChange = useCallback((value: string) => {
        const v = value?.trim() ?? "";
        setStatusFilter(v || null);
        requestAnimationFrame(() => tableRef.current?.reload?.());
    }, []);

    const handleSearchApply = useCallback(() => {
        setAppliedSearchKeyword(searchInputValue.trim());
        requestAnimationFrame(() => tableRef.current?.reload?.());
    }, [searchInputValue]);

    const handleSearchClear = useCallback(() => {
        setSearchInputValue("");
        setAppliedSearchKeyword("");
        requestAnimationFrame(() => tableRef.current?.reload?.());
    }, []);

    const handleTabChange = useCallback((key: string) => {
        setActiveTab(key as FlowTabKey);
    }, []);

    const disableWorkflow = useCallback(
        async (record: WorkflowRecord) => {
            if (!appProperties || !record.workflowId) return;
            const raw = await fetchData(`workflows/${record.workflowId}/disable`, "POST", null, appProperties);
            if (raw == null) {
                message.error("Failed to disable rule. Please try again.");
                return;
            }
            const env = parseFetchDataEnvelope(raw);
            if (isWorkflowApiLogicalFailure(env.axiosBody) || isWorkflowApiLogicalFailure(env.entity)) {
                message.error(
                    getWorkflowApiErrorMessage(env.entity, env.axiosBody) ??
                        "Failed to disable rule. Please try again."
                );
                return;
            }
            successNotification("Rule disabled successfully.");
            reload();
        },
        [appProperties, fetchData, reload]
    );

    const enableWorkflow = useCallback(
        async (record: WorkflowRecord) => {
            if (!appProperties || !record.workflowId) return;
            const raw = await fetchData(`workflows/${record.workflowId}/enable`, "POST", null, appProperties);
            if (raw == null) {
                message.error("Failed to enable rule. Please try again.");
                return;
            }
            const env = parseFetchDataEnvelope(raw);
            if (isWorkflowApiLogicalFailure(env.axiosBody) || isWorkflowApiLogicalFailure(env.entity)) {
                message.error(
                    getWorkflowApiErrorMessage(env.entity, env.axiosBody) ??
                        "Failed to enable rule. Please try again."
                );
                return;
            }
            successNotification("Rule enabled successfully.");
            reload();
        },
        [appProperties, fetchData, reload]
    );

    const deleteWorkflow = useCallback(
        async (record: WorkflowRecord) => {
            if (!appProperties || !record.workflowId) return;
            const raw = await fetchData(`workflows/${record.workflowId}`, "DELETE", null, appProperties);
            if (raw == null) {
                message.error("Failed to delete rule. Please try again.");
                return;
            }
            const env = parseFetchDataEnvelope(raw);
            if (isWorkflowApiLogicalFailure(env.axiosBody) || isWorkflowApiLogicalFailure(env.entity)) {
                message.error(
                    getWorkflowApiErrorMessage(env.entity, env.axiosBody) ??
                        "Failed to delete rule. Please try again."
                );
                return;
            }
            successNotification("Rule deleted successfully.");
            reload();
        },
        [appProperties, fetchData, reload]
    );

    return {
        requestWorkflows,
        activeTab,
        setActiveTab,
        searchInputValue,
        setSearchInputValue,
        appliedSearchKeyword,
        triggerTypeFilter,
        statusFilter,
        handleTriggerTypeFilterChange,
        handleStatusFilterChange,
        handleSearchApply,
        handleSearchClear,
        handleTabChange,
        enableWorkflow,
        disableWorkflow,
        deleteWorkflow,
        reload,
        tableRef,
        currentPageData,
    };
}

export interface UseWorkflowResult {
    workflow: ServerWorkflow | null;
    isLoading: boolean;
    error: Error | null;
    refetch: () => Promise<void>;
}

export function useWorkflow(workflowId: string | null | undefined): UseWorkflowResult {
    const [appProperties] = useContext(AppContext);
    const { fetchData } = useHttp();
    const [workflow, setWorkflow] = useState<ServerWorkflow | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);

    useLayoutEffect(() => {
        if (!workflowId) {
            setWorkflow(null);
            setIsLoading(false);
            setError(null);
            return;
        }
        setWorkflow(null);
        setIsLoading(true);
        setError(null);
    }, [workflowId]);

    const loadWorkflow = useCallback(async () => {
        if (!workflowId || !appProperties) {
            setWorkflow(null);
            setIsLoading(false);
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            const response = await fetchData(`workflows/${workflowId}`, "GET", null, appProperties);
            if (!response) {
                setWorkflow(null);
                const errText = "Failed to load rule. Please try again.";
                setError(new Error(errText));
                message.error(errText);
                return;
            }

            const env = parseFetchDataEnvelope(response);
            if (isWorkflowApiLogicalFailure(env.axiosBody) || isWorkflowApiLogicalFailure(env.entity)) {
                const errText =
                    getWorkflowApiErrorMessage(env.entity, env.axiosBody) ?? "Failed to load rule.";
                message.error(errText);
                setError(new Error(errText));
                setWorkflow(null);
                return;
            }

            const rawPayload = extractWorkflowPayload(env);
            const payload = rawPayload as ServerWorkflow | null;

            if (payload?.graph) {
                setWorkflow(payload);
            } else {
                setWorkflow(null);
            }
        } catch (e) {
            setError(e instanceof Error ? e : new Error(String(e)));
            setWorkflow(null);
            message.error("Failed to load rule. Please try again.");
        } finally {
            setIsLoading(false);
        }
    }, [workflowId, appProperties, fetchData]);

    useEffect(() => {
        loadWorkflow();
    }, [loadWorkflow]);

    return { workflow, isLoading, error, refetch: loadWorkflow };
}
